import type { z } from 'zod'
import { strictify } from '~/utils/strictify'
import type { UpdateStoreMenusOrderRequest } from '~/services/api/interfaces/Store'
import * as operations from '~/services/api/generated'
import { StoreStatusSchema } from '~/services/api/generated'
import { collectPagedItems } from '~/utils/collectPagedItems'
import { requestAndParse } from '~/services/api/requestAndParse'
import { createRequestOperation } from '~/utils/createRequestOperation'
import { responseData } from '~/utils/responseData'
import { createDeleteOperation } from '~/services/api/createDeleteOperation'
import { buildMultipartBody } from '~/utils/buildMultipartBody'
import { StoresDashboard } from '~/services/api/contracts/requests/StoresDashboard'
import { StoreStore } from '~/services/api/contracts/requests/StoreStore'
import { StoreUpdateDetails } from '~/services/api/contracts/requests/StoreUpdateDetails'
import { StoreUpdateServices } from '~/services/api/contracts/requests/StoreUpdateServices'
import { StoreUpdateHours } from '~/services/api/contracts/requests/StoreUpdateHours'
import { StoreUpdatePricing } from '~/services/api/contracts/requests/StoreUpdatePricing'
import { StoreUpdateLeadTime } from '~/services/api/contracts/requests/StoreUpdateLeadTime'
import { StoreUpdateImage } from '~/services/api/contracts/requests/StoreUpdateImage'
import { StoreUpdateTemporaryClosures } from '~/services/api/contracts/requests/StoreUpdateTemporaryClosures'
import { MenuUpdateStoreMenuOrder } from '~/services/api/contracts/requests/MenuUpdateStoreMenuOrder'
import { StoreReviewSubmitTokenReview } from '~/services/api/contracts/requests/StoreReviewSubmitTokenReview'
import { StoreUpdateStatus } from '~/services/api/contracts/requests/StoreUpdateStatus'
import { StoreUpdateVenue } from '~/services/api/contracts/requests/StoreUpdateVenue'
import { formatStoreAddress } from '~/utils/formatStoreAddress'
import type { PublicStore, StoreListItem } from './interfaces'

type DashboardStoresSuccess = operations.StoresDashboardSuccess
type DashboardStoreSummary = DashboardStoresSuccess['data']['items'][number]
type StoreMenusSuccess = operations.MenuStoreMenusSuccess
type StoreMenu = StoreMenusSuccess['data']['menus'][number]

export function useStoreService() {
  const dashboardBaseURL = `${useRuntimeConfig().public.apiBaseUrl ?? ''}/api/dashboard`
  const publicBaseURL = `${useRuntimeConfig().public.apiBaseUrl ?? ''}/api`
  const sanctumClient = useApiRequest()
  const destroy = createDeleteOperation(
    sanctumClient,
    (identifier) => operations.StoreDestroyPathSchema.strict().parse({ identifier }),
    operations.StoreDestroySuccessSchema,
    ({ identifier }) => `${dashboardBaseURL}/stores/${identifier}`
  )
  const destroyTemporaryClosure = createDeleteOperation(
    sanctumClient,
    (identifier, temporaryClosure) =>
      operations.StoreDestroyTemporaryClosurePathSchema.strict().parse({
        identifier,
        temporaryClosure
      }),
    operations.StoreDestroyTemporaryClosureSuccessSchema,
    ({ identifier, temporaryClosure }) =>
      `${dashboardBaseURL}/stores/${identifier}/temporary-closures/${temporaryClosure}`
  )

  const storeUrl = (identifier: string, segment: string): string =>
    `${dashboardBaseURL}/stores/${identifier}/${segment}`

  const normalizeStoreListItem = (store: DashboardStoreSummary): StoreListItem => ({
    id: store.id,
    name: store.name,
    slug: store.slug,
    status: StoreStatusSchema.parse(store.status),
    average_rating: store.average_rating,
    brand_id: null,
    brand_name: null,
    brand: null,
    address: store.address,
    formatted_address: formatStoreAddress(store),
    city: store.city,
    state: store.state,
    logo: store.logo,
    cover: store.cover
  })

  async function getStoresPaginated(params?: operations.StoresDashboardQuery) {
    const query = StoresDashboard({})
      .strict()
      .parse(params ?? {})
    const response = strictify(operations.StoresDashboardSuccessSchema).parse(
      await sanctumClient(`${dashboardBaseURL}/stores`, {
        method: 'GET',
        params: query
      })
    )
    return {
      items: response.data.items.map(normalizeStoreListItem),
      pagination: response.data.pagination
    }
  }

  /** Every store across all dashboard pages. */
  const getStores = (): Promise<StoreListItem[]> =>
    collectPagedItems(async (page) => {
      const response = await getStoresPaginated({ page })
      return { items: response.items, lastPage: response.pagination.last_page }
    })

  /**
   * Fetch a single store by ID.
   */
  async function show(
    identifier: operations.DashboardStoreShowPath['identifier']
  ): Promise<operations.StoreDto> {
    const path = operations.DashboardStoreShowPathSchema.strict().parse({ identifier })
    const response = operations.DashboardStoreShowSuccessSchema.parse(
      await sanctumClient(`${dashboardBaseURL}/stores/${path.identifier}`)
    )
    return response.data
  }

  /**
   * Operational health for an active store (rating, reliability, badges).
   */
  async function health(
    identifier: operations.StoreHealthPath['identifier']
  ): Promise<operations.StoreHealthSuccess['data']> {
    const path = operations.StoreHealthPathSchema.strict().parse({ identifier })
    const response = await requestAndParse(
      sanctumClient,
      operations.StoreHealthSuccessSchema,
      `${dashboardBaseURL}/stores/${path.identifier}/health`
    )
    return response.data
  }

  async function setupProgress(
    identifier: operations.StoreSetupProgressPath['identifier']
  ): Promise<operations.StoreSetupProgressSuccess['data']> {
    const path = operations.StoreSetupProgressPathSchema.strict().parse({ identifier })
    const response = strictify(operations.StoreSetupProgressSuccessSchema).parse(
      await sanctumClient(`${dashboardBaseURL}/stores/${path.identifier}/setup-progress`)
    )
    return response.data
  }

  const normalizeStoreMenus = (items: StoreMenu[]): StoreMenu[] =>
    [...items].sort((first, second) => first.sort_order - second.sort_order)

  async function menus(
    identifier: operations.MenuStoreMenusPath['identifier']
  ): Promise<StoreMenu[]> {
    const path = operations.MenuStoreMenusPathSchema.strict().parse({ identifier })
    const response = strictify(operations.MenuStoreMenusSuccessSchema).parse(
      await sanctumClient(`${dashboardBaseURL}/stores/${path.identifier}/menus`)
    )
    return normalizeStoreMenus(response.data.menus)
  }

  async function create(
    payload: z.input<ReturnType<typeof StoreStore>>
  ): Promise<operations.StoreDto> {
    const request = StoreStore({}).parse(payload)
    const body = buildMultipartBody(request)

    const response = operations.StoreStoreSuccessSchema.parse(
      await sanctumClient(`${dashboardBaseURL}/stores`, {
        method: 'POST',
        body
      })
    )
    return response.data
  }

  const updateDetails = createRequestOperation(
    sanctumClient,
    strictify(operations.StoreUpdateDetailsSuccessSchema),
    (
      identifier: operations.StoreUpdateDetailsPath['identifier'],
      payload: z.output<ReturnType<typeof StoreUpdateDetails>>,
      context: Parameters<typeof StoreUpdateDetails>[0]
    ) => ({
      url: storeUrl(
        operations.StoreUpdateDetailsPathSchema.strict().parse({ identifier }).identifier,
        'details'
      ),
      options: {
        method: 'PUT',
        body: buildMultipartBody(StoreUpdateDetails(context).parse(payload))
      }
    }),
    responseData
  )

  const updateServices = createRequestOperation(
    sanctumClient,
    strictify(operations.StoreUpdateServicesSuccessSchema),
    (
      identifier: operations.StoreUpdateServicesPath['identifier'],
      payload: z.output<ReturnType<typeof StoreUpdateServices>>,
      context: Parameters<typeof StoreUpdateServices>[0]
    ) => ({
      url: storeUrl(
        operations.StoreUpdateServicesPathSchema.strict().parse({ identifier }).identifier,
        'services'
      ),
      options: { method: 'PUT', body: StoreUpdateServices(context).parse(payload) }
    }),
    responseData
  )

  const updateHours = createRequestOperation(
    sanctumClient,
    strictify(operations.StoreUpdateHoursSuccessSchema),
    (
      identifier: operations.StoreUpdateHoursPath['identifier'],
      payload: z.output<ReturnType<typeof StoreUpdateHours>>
    ) => ({
      url: storeUrl(
        operations.StoreUpdateHoursPathSchema.strict().parse({ identifier }).identifier,
        'hours'
      ),
      options: { method: 'PUT', body: StoreUpdateHours({}).parse(payload) }
    }),
    responseData
  )

  const updatePricing = createRequestOperation(
    sanctumClient,
    strictify(operations.StoreUpdatePricingSuccessSchema),
    (
      identifier: operations.StoreUpdatePricingPath['identifier'],
      payload: z.output<ReturnType<typeof StoreUpdatePricing>>
    ) => ({
      url: storeUrl(
        operations.StoreUpdatePricingPathSchema.strict().parse({ identifier }).identifier,
        'pricing'
      ),
      options: { method: 'PUT', body: StoreUpdatePricing({}).parse(payload) }
    }),
    responseData
  )

  const updateLeadTime = createRequestOperation(
    sanctumClient,
    strictify(operations.StoreUpdateLeadTimeSuccessSchema),
    (
      identifier: operations.StoreUpdateLeadTimePath['identifier'],
      payload: z.output<ReturnType<typeof StoreUpdateLeadTime>>
    ) => ({
      url: storeUrl(
        operations.StoreUpdateLeadTimePathSchema.strict().parse({ identifier }).identifier,
        'lead-time'
      ),
      options: { method: 'PUT', body: StoreUpdateLeadTime({}).parse(payload) }
    }),
    responseData
  )

  const updateImage = createRequestOperation(
    sanctumClient,
    strictify(operations.StoreUpdateImageSuccessSchema),
    (
      identifier: operations.StoreUpdateImagePath['identifier'],
      payload: z.output<ReturnType<typeof StoreUpdateImage>>
    ) => ({
      url: storeUrl(
        operations.StoreUpdateImagePathSchema.strict().parse({ identifier }).identifier,
        'image'
      ),
      options: { method: 'POST', body: buildMultipartBody(StoreUpdateImage({}).parse(payload)) }
    }),
    responseData
  )

  async function submit(
    identifier: operations.StoreSubmitPath['identifier']
  ): Promise<operations.StoreDto> {
    const path = operations.StoreSubmitPathSchema.strict().parse({ identifier })
    const response = operations.StoreSubmitSuccessSchema.parse(
      await sanctumClient(`${dashboardBaseURL}/stores/${path.identifier}/submit`, {
        method: 'POST'
      })
    )
    return response.data
  }

  /**
   * Duplicate a store — creates a draft copy with no location details.
   */
  async function duplicate(
    identifier: operations.StoreDuplicatePath['identifier']
  ): Promise<operations.StoreDto> {
    const path = operations.StoreDuplicatePathSchema.strict().parse({ identifier })
    const response = operations.StoreDuplicateSuccessSchema.parse(
      await sanctumClient(`${dashboardBaseURL}/stores/${path.identifier}/duplicate`, {
        method: 'POST'
      })
    )
    return response.data
  }

  const updateStatus = createRequestOperation(
    sanctumClient,
    strictify(operations.StoreUpdateStatusSuccessSchema),
    (
      identifier: operations.StoreUpdateStatusPath['identifier'],
      status: operations.StoreUpdateStatusRequest['status']
    ) => ({
      url: storeUrl(
        operations.StoreUpdateStatusPathSchema.strict().parse({ identifier }).identifier,
        'status'
      ),
      options: { method: 'PUT', body: StoreUpdateStatus({}).parse({ status }) }
    }),
    responseData
  )

  const updateVenue = createRequestOperation(
    sanctumClient,
    strictify(operations.StoreUpdateVenueSuccessSchema),
    (
      identifier: operations.StoreUpdateVenuePath['identifier'],
      payload: z.output<ReturnType<typeof StoreUpdateVenue>>
    ) => ({
      url: storeUrl(
        operations.StoreUpdateVenuePathSchema.strict().parse({ identifier }).identifier,
        'venue'
      ),
      options: { method: 'PUT', body: StoreUpdateVenue({}).parse(payload) }
    }),
    responseData
  )

  async function temporaryClosures(
    identifier: operations.StoreTemporaryClosuresPath['identifier']
  ): Promise<operations.StoreTemporaryClosure[]> {
    const path = operations.StoreTemporaryClosuresPathSchema.strict().parse({ identifier })
    const response = strictify(operations.StoreTemporaryClosuresSuccessSchema).parse(
      await sanctumClient(`${dashboardBaseURL}/stores/${path.identifier}/temporary-closures`)
    )
    return response.data.items
  }

  const updateTemporaryClosures = createRequestOperation(
    sanctumClient,
    strictify(operations.StoreUpdateTemporaryClosuresSuccessSchema),
    (
      identifier: operations.StoreUpdateTemporaryClosuresPath['identifier'],
      payload: z.output<ReturnType<typeof StoreUpdateTemporaryClosures>>,
      context: Parameters<typeof StoreUpdateTemporaryClosures>[0]
    ) => ({
      url: storeUrl(
        operations.StoreUpdateTemporaryClosuresPathSchema.strict().parse({ identifier }).identifier,
        'temporary-closures'
      ),
      options: { method: 'PUT', body: StoreUpdateTemporaryClosures(context).parse(payload) }
    }),
    (response) => response.data.items
  )

  async function getReviewForm(
    token: operations.StoreReviewShowPath['token']
  ): Promise<operations.StoreReviewForm> {
    const path = operations.StoreReviewShowPathSchema.strict().parse({ token })
    const response = strictify(operations.StoreReviewShowSuccessSchema).parse(
      await $fetch(`${publicBaseURL}/stores/reviews/${encodeURIComponent(path.token)}`)
    )
    return response.data
  }

  async function getPublicStoreBySlug(
    slug: operations.StoreShowQuery['slug'],
    userId?: operations.StoreShowQuery['userId'],
    requestedDate?: operations.StoreShowQuery['requested_date'],
    requestedTime?: operations.StoreShowQuery['requested_time']
  ): Promise<PublicStore> {
    const query = {
      slug,
      ...(userId === undefined ? {} : { userId }),
      ...(requestedDate == null ? {} : { requested_date: requestedDate }),
      ...(requestedTime == null ? {} : { requested_time: requestedTime })
    }
    // include_reviews defaults to 'false' on the schema; validated but never
    // sent explicitly so the wire query only ever carries what the caller set.
    const { include_reviews: _includeReviews, ...validatedQuery } =
      operations.StoreShowQuerySchema.strict().parse(query)
    const response = operations.StoreShowSuccessSchema.parse(
      await $fetch(`${publicBaseURL}/stores`, { query: validatedQuery })
    )
    const store = response.data
    // A null average_rating means no rating exists; review_count is forced to 0 alongside it so
    // a wire inconsistency (a stray positive count with no rating) can never render a blank number.
    return {
      ...store,
      review_count: store.average_rating === null ? 0 : (store.review_count ?? 0)
    }
  }

  async function submitReview(
    token: operations.StoreReviewSubmitTokenReviewPath['token'],
    payload: z.output<ReturnType<typeof StoreReviewSubmitTokenReview>>
  ): Promise<operations.StoreReviewSubmitTokenReviewSuccess> {
    const path = operations.StoreReviewSubmitTokenReviewPathSchema.strict().parse({ token })
    const request = StoreReviewSubmitTokenReview({}).parse(payload)
    const response = strictify(operations.StoreReviewSubmitTokenReviewSuccessSchema).parse(
      await sanctumClient(`${publicBaseURL}/stores/reviews/${encodeURIComponent(path.token)}`, {
        method: 'POST',
        body: request
      })
    )
    return response
  }

  async function getStoreReviews(
    slug: string,
    params?: { rating?: number; page?: number }
  ): Promise<operations.StoreReviewIndexSuccess> {
    const query = {
      slug,
      ...(params?.rating === undefined ? {} : { rating: params.rating }),
      ...(params?.page === undefined ? {} : { page: params.page })
    }
    const validatedQuery = operations.StoreReviewIndexQuerySchema.strict().parse(query)
    const response = operations.StoreReviewIndexSuccessSchema.parse(
      await $fetch(`${publicBaseURL}/stores/reviews`, { query: validatedQuery })
    )
    return response
  }

  async function getStoreGoogleReviews(
    slug: string,
    params?: { rating?: number; page?: number }
  ): Promise<operations.StoreReviewGoogleReviewsSuccess> {
    const query = {
      slug,
      ...(params?.rating === undefined ? {} : { rating: params.rating }),
      ...(params?.page === undefined ? {} : { page: params.page })
    }
    const validatedQuery = operations.StoreReviewGoogleReviewsQuerySchema.strict().parse(query)
    const response = operations.StoreReviewGoogleReviewsSuccessSchema.parse(
      await $fetch(`${publicBaseURL}/stores/google-reviews`, { query: validatedQuery })
    )
    return response
  }

  async function updateMenusOrder(
    identifier: operations.MenuUpdateStoreMenuOrderPath['identifier'],
    payload: UpdateStoreMenusOrderRequest
  ): Promise<operations.MenuUpdateStoreMenuOrderSuccess> {
    const path = operations.MenuUpdateStoreMenuOrderPathSchema.strict().parse({ identifier })
    const request = MenuUpdateStoreMenuOrder({}).parse(payload)
    const response = strictify(operations.MenuUpdateStoreMenuOrderSuccessSchema).parse(
      await sanctumClient(`${dashboardBaseURL}/stores/${path.identifier}/menus/order`, {
        method: 'PUT',
        body: request
      })
    )
    return response
  }

  return {
    getStores,
    getStoresPaginated,
    show,
    health,
    setupProgress,
    menus,
    create,
    destroy,
    updateDetails,
    updateServices,
    updateHours,
    updatePricing,
    updateLeadTime,
    updateImage,
    submit,
    duplicate,
    updateStatus,
    updateVenue,
    temporaryClosures,
    updateTemporaryClosures,
    destroyTemporaryClosure,
    getReviewForm,
    getPublicStoreBySlug,
    submitReview,
    getStoreReviews,
    getStoreGoogleReviews,
    updateMenusOrder
  }
}
